/**
 * COMPTABILITÉ — LE CALENDRIER DES NUITS.
 *
 * Une case par nuit commerciale. Les soirées Shotgun et les rapports ZAP s'y
 * posent seuls ; la couleur dit si la nuit est faite, l'icône et le texte le
 * redisent. Un clic ouvre LA NUIT (une vraie page, pas un tiroir).
 *
 * Ce que l'écran ne fait PAS : décider d'un état. Le serveur rend chaque nuit
 * avec son état, calculé par le même moteur que l'accueil et le récapitulatif
 * du mardi — le calendrier ne peut donc pas les contredire.
 *
 * ═══ LA LIGNE DE SEMAINE ═══
 *
 * Sous chaque semaine : ce que ZAP a ANNONCÉ (bars et vestiaire, hors offerts),
 * les offerts, et les nuits à faire. « Annoncé » parce que ce n'est pas encore
 * un chiffre arrêté : seul le retenu d'une clôture finalisée l'est — ou le
 * chiffre d'une nuit validée sans clôture détaillée, que sa case écrit à part.
 *
 * ═══ LES VENTES DU GUICHET D'UNE AUTRE NUIT (08/10/2026) ═══
 *
 * Une caisse Shotgun restée ouverte sur une ancienne soirée fausse l'Accueil
 * de DEUX nuits : celle de la vente, celle de la soirée. Le serveur pose le
 * drapeau `GUICHET_HORS_NUIT` sur les deux ; la case le montre (icône et
 * texte, ambre tant qu'une décision est attendue), la légende l'explique.
 * C'est ainsi que la DAF le voit sans le chercher à la main.
 *
 * ═══ L'ADRESSE PORTE LE MOIS ET LA VUE ═══
 *
 * `?mois=2026-09&vue=liste` : on ouvre une nuit, on revient en arrière, et le
 * calendrier est là où on l'avait laissé — pas sur le mois courant. L'adresse
 * est RÉÉCRITE (`remplacerRecherche`, sans nouvelle entrée d'historique) à
 * chaque changement : feuilleter six mois ne doit pas demander six « Retour ».
 * Pas `redirect` : réécrite dès le montage, elle marquait l'arrivée comme un
 * remplacement et la page ne s'ouvrait plus en haut (voir adresse-ecran.ts).
 * Le mois courant et la vue par défaut ne s'écrivent pas : l'adresse reste
 * nue tant que l'écran est celui qu'on obtiendrait sans elle.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { ArrowLeftRight, CalendarDays, ChevronLeft, ChevronRight, List, Grid3X3, ArrowRight } from 'lucide-react'
import { Link } from '../router.js'
import { SmartBack } from '../navigation/SmartBack.js'
import { SegmentedControl } from '../admin/pickers.js'
import { remplacerRecherche } from './adresse-ecran.js'
import { repriseDe } from './reprise-historique.js'
import { moisValide } from './date-adresse.js'
import { EnCours, Indisponible, aujourdhuiParis, lireJson, type Etat } from './ClosingCommon.js'
import {
  EtatNuit, JOURS_SEMAINE, NIGHT_PAINT, commeNuits, debutDeMois, jourCourt, libelleMois, montantNuit,
  moisSuivant, numeroDuJour, semainesDuMois,
  type NightRow, type NightState, type NightsResponse, type NightsWeekRow,
} from './nights-common.js'
import { nuitDeLigne, useMenuSoiree } from '../soirees/menu-soiree.js'
import './finance-nights.css'

type Vue = 'mois' | 'liste'

/** L'ordre de la légende : ce qui demande un geste d'abord. */
const LEGENDE: NightState[] = ['EN_RETARD', 'SOIREE_SANS_RAPPORT', 'A_CLOTURER', 'RAPPORT_ATTENDU', 'CLOTUREE', 'VALIDEE', 'FERMEE', 'A_VENIR']

function vueParDefaut(): Vue {
  // Sous 760 px, sept colonnes ne laissent plus lire un nom de soirée : la
  // liste dit la même chose, lisiblement.
  try { return window.matchMedia('(max-width: 760px)').matches ? 'liste' : 'mois' } catch { return 'mois' }
}

/** Le mois et la vue lus dans l'adresse ; à défaut, le mois courant et la vue qui tient à l'écran. */
function etatInitial(): { mois: string; vue: Vue } {
  const q = new URLSearchParams(window.location.search)
  const m = q.get('mois')
  const v = q.get('vue')
  return {
    // Un mois RÉEL : « 2026-13 » a la forme d'un mois, et faisait lever le rendu.
    mois: moisValide(m) ? `${m}-01` : debutDeMois(aujourdhuiParis()),
    vue: v === 'liste' || v === 'mois' ? v : vueParDefaut(),
  }
}

export function FinanceNightsCalendarPage() {
  const initial = useMemo(etatInitial, [])
  const [mois, setMois] = useState(initial.mois)
  const [vue, setVue] = useState<Vue>(initial.vue)
  const [donnees, setDonnees] = useState<Etat<NightsResponse>>({ etat: 'chargement' })
  const menuSoiree = useMenuSoiree()

  // L'adresse suit l'écran, en REMPLAÇANT l'entrée d'historique (voir l'en-tête).
  useEffect(() => {
    const q = new URLSearchParams()
    if (mois !== debutDeMois(aujourdhuiParis())) q.set('mois', mois.slice(0, 7))
    if (vue !== vueParDefaut()) q.set('vue', vue)
    remplacerRecherche('/comptabilite/nuits', q)
  }, [mois, vue])

  const semaines = useMemo(() => semainesDuMois(mois), [mois])
  const premier = semaines[0]![0]!
  const dernier = semaines[semaines.length - 1]![6]!

  const charger = useCallback(() => {
    setDonnees({ etat: 'chargement' })
    void lireJson<NightsResponse>(`/api/v1/finance/nights?from=${premier}&to=${dernier}`, commeNuits).then(setDonnees)
  }, [premier, dernier])
  useEffect(charger, [charger])

  const parNuit = useMemo(() => {
    const m = new Map<string, NightRow>()
    if (donnees.etat === 'ok') for (const n of donnees.valeur.nights) m.set(n.business_date, n)
    return m
  }, [donnees])
  const parSemaine = useMemo(() => {
    const m = new Map<string, NightsWeekRow>()
    if (donnees.etat === 'ok') for (const w of donnees.valeur.weeks) m.set(w.week_start, w)
    return m
  }, [donnees])

  const courante = donnees.etat === 'ok' ? donnees.valeur.current_night : null
  const aFaire = donnees.etat === 'ok' ? donnees.valeur.oldest_pending : null
  const nuitsDuMois = [...parNuit.values()].filter((n) => n.business_date.startsWith(mois.slice(0, 7)))

  return (
    <main className="lc-page lc-page--large lc-stagger fn-page">
      <SmartBack />
      <header className="fh-hero">
        <span className="fh-hero__icon" aria-hidden="true"><CalendarDays size={26} /></span>
        <div>
          <h1 className="lc-title">Calendrier des nuits</h1>
          <p className="lc-subtitle">
            Chaque nuit, ses soirées et son rapport de caisse. Vert : clôturée, ou validée sans
            clôture détaillée. Orange : à clôturer. Rouge : en retard, ou une soirée sans rapport ZAP.
          </p>
        </div>
      </header>

      {aFaire ? (
        <section className="lc-panel fn-afaire">
          <div className="lc-panel__body fn-afaire__corps">
            <EtatNuit state={aFaire.state} libelle={aFaire.state_label} />
            <div className="fn-afaire__texte">
              <p className="fn-afaire__titre">Plus ancienne nuit à traiter : {jourCourt(aFaire.business_date)}</p>
              <p className="lc-hint">
                {aFaire.soirees.length > 0 ? aFaire.soirees.map((s) => s.name).join(' · ') : 'Aucune soirée déclarée'}
              </p>
            </div>
            <Link className="lc-btn lc-btn--primary" href={`/comptabilite/nuits/${aFaire.business_date}`} {...menuSoiree.nuit(nuitDeLigne(aFaire))}>
              Ouvrir la nuit <ArrowRight size={16} aria-hidden="true" />
            </Link>
          </div>
        </section>
      ) : null}

      <section className="lc-panel">
        <div className="lc-panel__head fn-barre">
          <div className="fn-barre__nav">
            <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" aria-label="Mois précédent" onClick={() => setMois(moisSuivant(mois, -1))}>
              <ChevronLeft size={16} aria-hidden="true" />
            </button>
            <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={() => setMois(debutDeMois(aujourdhuiParis()))}>
              Aujourd’hui
            </button>
            <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" aria-label="Mois suivant" onClick={() => setMois(moisSuivant(mois, 1))}>
              <ChevronRight size={16} aria-hidden="true" />
            </button>
            <h2 className="fn-barre__mois">{libelleMois(mois)}</h2>
          </div>
          <span className="lc-panel__spacer" />
          <SegmentedControl<Vue>
            size="sm" ariaLabel="Affichage" value={vue} onChange={setVue}
            options={[
              { value: 'mois', label: 'Mois', icon: <Grid3X3 size={14} /> },
              { value: 'liste', label: 'Liste', icon: <List size={14} /> },
            ]}
          />
        </div>

        <div className="lc-panel__body">
          <ul className="fn-legende" aria-label="Légende">
            {LEGENDE.map((s) => <li key={s}><EtatNuit state={s} court /></li>)}
            <li><DrapeauHorsNuit attente /></li>
          </ul>

          {donnees.etat === 'chargement' ? <EnCours quoi="des nuits" /> : null}
          {donnees.etat === 'absent' ? (
            <Indisponible titre="Les nuits n’ont pas pu être lues" message={donnees.message}>
              <button className="lc-btn lc-btn--sm" onClick={charger}>Réessayer</button>
            </Indisponible>
          ) : null}

          {donnees.etat === 'ok' && vue === 'mois' ? (
            <div className="fn-cal" role="grid" aria-label={`Nuits de ${libelleMois(mois)}`}>
              <div className="fn-cal__jours" role="row">
                {JOURS_SEMAINE.map((j) => <span key={j} className="fn-cal__jour" role="columnheader">{j}</span>)}
              </div>
              {semaines.map((semaine) => {
                const w = parSemaine.get(semaine[0]!)
                return (
                  <div key={semaine[0]} className="fn-cal__semaine">
                    <div className="fn-cal__ligne" role="row">
                      {semaine.map((jour) => (
                        <CaseNuit
                          key={jour} jour={jour} nuit={parNuit.get(jour) ?? null}
                          horsMois={!jour.startsWith(mois.slice(0, 7))} courante={jour === courante} menu={menuSoiree}
                        />
                      ))}
                    </div>
                    {w ? <LigneSemaine semaine={w} /> : null}
                  </div>
                )
              })}
            </div>
          ) : null}

          {donnees.etat === 'ok' && vue === 'liste' ? (
            <ul className="fn-liste">
              {nuitsDuMois.slice().reverse().map((n) => (
                <li key={n.business_date}>
                  <Link className={`fn-liste__ligne fn-ton--${NIGHT_PAINT[n.state].tone}`} href={`/comptabilite/nuits/${n.business_date}`} {...menuSoiree.nuit(nuitDeLigne(n))}>
                    <span className="fn-liste__date">{jourCourt(n.business_date)}</span>
                    <span className="fn-liste__soiree">
                      {n.soirees.length > 0 ? n.soirees.map((s) => s.name).join(' · ') : '—'}
                    </span>
                    <span className="fn-liste__montant fi-money">{montantNuit(n.zap.announced_cents)}</span>
                    <span className="fn-liste__etats">
                      <EtatNuit state={n.state} libelle={n.state_label} />
                      {n.flags.includes(DRAPEAU_HORS_NUIT) ? <DrapeauHorsNuit attente={horsNuitEnAttente(n)} /> : null}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </section>
    </main>
  )
}

/** Le drapeau serveur des ventes du guichet faites pendant une autre nuit. */
const DRAPEAU_HORS_NUIT = 'GUICHET_HORS_NUIT'

/** Une décision est-elle encore attendue sur l'une des ventes hors nuit de la nuit ? */
const horsNuitEnAttente = (n: NightRow): boolean => (n.off_night_sales ?? []).some((c) => c.status === 'EN_ATTENTE')

/**
 * LE DRAPEAU DANS UNE CASE — une icône ET un texte (jamais la couleur seule).
 * Ambre tant qu'une décision est attendue ; neutre une fois décidé.
 */
function DrapeauHorsNuit(props: { attente: boolean }) {
  return (
    <span className={`fn-drapeau${props.attente ? ' fn-drapeau--attente' : ''}`}>
      <ArrowLeftRight size={13} aria-hidden="true" />
      <span>{props.attente ? 'Guichet d’une autre nuit' : 'Guichet d’une autre nuit, décidé'}</span>
    </span>
  )
}

/** `menu` : le menu clic droit de la page, lu une fois pour toutes ses cases. */
function CaseNuit(props: { jour: string; nuit: NightRow | null; horsMois: boolean; courante: boolean; menu: ReturnType<typeof useMenuSoiree> }) {
  const n = props.nuit
  const p = n ? NIGHT_PAINT[n.state] : null
  const soiree = n?.soirees.find((s) => s.status === 'SCHEDULED') ?? n?.soirees[0] ?? null
  const horsNuit = n !== null && n.flags.includes(DRAPEAU_HORS_NUIT)
  const classes = [
    'fn-case',
    p ? `fn-ton--${p.tone}` : 'fn-ton--idle',
    props.horsMois ? 'fn-case--hors' : '',
    props.courante ? 'fn-case--courante' : '',
  ].filter(Boolean).join(' ')
  // L'état est DIT dans l'étiquette accessible : la couleur n'en est que l'écho.
  const aria = `${props.jour} — ${n ? n.state_label : 'nuit sans donnée'}${soiree ? `, ${soiree.name}` : ''}${horsNuit ? ', ventes du guichet d’une autre nuit' : ''}`
  return (
    <Link className={classes} href={`/comptabilite/nuits/${props.jour}`} aria-label={aria} role="gridcell" {...props.menu.nuit(n ? nuitDeLigne(n) : { date: props.jour })}>
      <span className="fn-case__tete">
        <span className="fn-case__num">{numeroDuJour(props.jour)}</span>
        {p && n && n.state !== 'FERMEE' && n.state !== 'A_VENIR' && n.state !== 'HORS_SUIVI'
          ? <EtatNuit state={n.state} court />
          : null}
      </span>
      {soiree ? (
        <span className={`fn-case__soiree${soiree.status === 'CANCELLED' ? ' fn-case__soiree--annulee' : ''}`}>
          {soiree.status === 'CANCELLED' ? `Annulée — ${soiree.name}` : soiree.name}
        </span>
      ) : null}
      {n && n.zap.announced_cents !== null ? (
        <span className="fn-case__montant fi-money">{montantNuit(n.zap.announced_cents)}</span>
      ) : null}
      {n && n.zap.complement_count > 0 ? (
        <span className="fn-case__note">+ {n.zap.complement_count} complément{n.zap.complement_count > 1 ? 's' : ''}</span>
      ) : null}
      {/* Le chiffre ARRÊTÉ en bloc s'écrit à part du montant annoncé par ZAP :
          les deux ne se confondent pas, et la ligne de semaine ne somme que l'annoncé. */}
      {n && n.validation !== null && n.validation.revenue_cents !== null ? (
        // Une nuit reprise de l'historique dit « Repris » : personne ne l'a
        // validée à l'écran. La saison et la source sont dans l'infobulle.
        repriseDe(n.validation.history) !== null
          ? <span className="fn-case__note" title={repriseDe(n.validation.history)!.label}>Repris {montantNuit(n.validation.revenue_cents)}</span>
          : <span className="fn-case__note">Validé {montantNuit(n.validation.revenue_cents)}</span>
      ) : null}
      {horsNuit ? <DrapeauHorsNuit attente={horsNuitEnAttente(n!)} /> : null}
    </Link>
  )
}

function LigneSemaine(props: { semaine: NightsWeekRow }) {
  const w = props.semaine
  return (
    <div className="fn-cal__resume">
      <span className="fn-cal__resume-cle">Semaine</span>
      <span>ZAP annoncé <strong className="fi-money">{montantNuit(w.zap_announced_cents)}</strong>{w.complete ? '' : ' (incomplet)'}</span>
      <span>Offerts <strong className="fi-money">{montantNuit(w.zap_offers_cents)}</strong></span>
      <span className={w.nights_todo > 0 ? 'fn-cal__resume-afaire' : ''}>
        {w.nights_todo === 0 ? 'Rien à faire' : `${w.nights_todo} nuit${w.nights_todo > 1 ? 's' : ''} à faire`}
      </span>
    </div>
  )
}
